"use client";

import { useCallback, useMemo } from "react";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import {
  FilterSelectInput,
  type FilterSelectInputOption,
} from "@/features/search/components/filters/input/select-input";

const GRAD_YEAR_NONE = "__none__";

const GRAD_YEAR_PAST_OFFSET = 75;
const GRAD_YEAR_FUTURE_OFFSET = 5;

function useGradYearSelectOptions(): FilterSelectInputOption<string>[] {
  return useMemo(() => {
    const now = new Date().getFullYear();
    const max = now + GRAD_YEAR_FUTURE_OFFSET;
    const min = now - GRAD_YEAR_PAST_OFFSET;
    const opts: FilterSelectInputOption<string>[] = [
      { value: GRAD_YEAR_NONE, label: "Any" },
    ];
    for (let y = max; y >= min; y -= 1) {
      opts.push({ value: String(y), label: String(y) });
    }
    return opts;
  }, []);
}

/** Graduation year range (maps to `grad_year_start` / `grad_year_end`). */
export function FilterGraduationYearBetweenInput() {
  const { filters, updateFilter } = useTalentSearchFilters();
  const options = useGradYearSelectOptions();
  const start = filters.grad_year_start;
  const end = filters.grad_year_end;

  const startValue = start != null ? String(start) : GRAD_YEAR_NONE;
  const endValue = end != null ? String(end) : GRAD_YEAR_NONE;

  const onStartChange = useCallback(
    (raw: string) => {
      if (raw === GRAD_YEAR_NONE) {
        updateFilter("grad_year_start", undefined);
        return;
      }
      const y = Number.parseInt(raw, 10);
      if (!Number.isFinite(y)) return;
      if (end != null && y > end) {
        updateFilter("grad_year_end", y);
      }
      updateFilter("grad_year_start", y);
    },
    [end, updateFilter],
  );

  const onEndChange = useCallback(
    (raw: string) => {
      if (raw === GRAD_YEAR_NONE) {
        updateFilter("grad_year_end", undefined);
        return;
      }
      const y = Number.parseInt(raw, 10);
      if (!Number.isFinite(y)) return;
      if (start != null && y < start) {
        updateFilter("grad_year_start", y);
      }
      updateFilter("grad_year_end", y);
    },
    [start, updateFilter],
  );

  return (
    <div className="flex items-center w-full min-w-0">
      <span className="text-sm font-normal text-contrast/80 whitespace-nowrap mr-1.5 shrink-0">
        Between
      </span>
      <div className="min-w-0 flex-1">
        <FilterSelectInput
          options={options}
          value={startValue}
          onChange={onStartChange}
          placeholder="Any"
        />
      </div>
      <span className="text-sm font-normal text-contrast/80 whitespace-nowrap mx-1.5 shrink-0">
        and
      </span>
      <div className="min-w-0 flex-1">
        <FilterSelectInput
          options={options}
          value={endValue}
          onChange={onEndChange}
          placeholder="Any"
        />
      </div>
    </div>
  );
}
